import { useState } from 'react';
import supabase from '../lib/supabase';
import YearMakeModelTrimPicker from './YearMakeModelTrimPicker';
import VehicleByImage from './VehicleByImage';
import AIEstimateGenerator from './estimate/AIEstimateGenerator';
import { decodeVIN, isValidVINFormat, formatVehicleLabel } from '../lib/vinDecoder';
import WMIcon from './ui/WMIcon';

const TABS = [
  { key: 'vin', label: 'VIN Lookup' },
  { key: 'browse', label: 'Browse' },
  { key: 'image', label: 'Image' },
];

export default function VinSearch({ onSelect, selectedCar }) {
  const [vin, setVin] = useState('5YJSA1E26TF123456');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchMode, setSearchMode] = useState('vin');
  const [searched, setSearched] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [nhtsaResult, setNhtsaResult] = useState(null); // decoded from NHTSA

  const handleVinSearch = async () => {
    if (vin.length !== 17) return;

    setLoading(true);
    setSearched(true);
    setNhtsaResult(null);

    // 1. Try Supabase DB first (has pricing/sq-ft data)
    try {
      const { data, error } = await supabase.rpc('find_car_by_vin', {
        p_vin: vin.toUpperCase(),
      });

      if (!error && data && (Array.isArray(data) ? data.length > 0 : data)) {
        setResults(Array.isArray(data) ? data : [data]);
        setLoading(false);
        return;
      }
    } catch { /* fall through to NHTSA */ }

    // 2. Fallback: NHTSA public API — free, no key, no CORS restriction
    if (isValidVINFormat(vin)) {
      try {
        const decoded = await decodeVIN(vin);
        if (decoded?.valid) {
          setNhtsaResult(decoded);
          setResults([]); // show NHTSA card instead
          setLoading(false);
          return;
        }
      } catch { /* ignore */ }
    }

    setResults([]);
    setLoading(false);
  };

  const handleNhtsaSelect = () => {
    if (!nhtsaResult) return;
    onSelect({
      year:         nhtsaResult.year ? parseInt(nhtsaResult.year) : null,
      make:         nhtsaResult.make,
      model:        nhtsaResult.model,
      trim:         nhtsaResult.trim || '',
      vehicle_type: nhtsaResult.bodyStyle || '',
      vin:          nhtsaResult.vin,
      label:        formatVehicleLabel(nhtsaResult),
      _fromNhtsa:   true,
    });
  };

  const handleVehicleSelect = (car) => {
    onSelect(car);
  };

  return (
    <div className="space-y-3">
      <div className="bg-white dark:bg-[#1B2A3E] rounded border border-gray-200 dark:border-[#243348] shadow-sm overflow-hidden">
        {/* Underline tab bar */}
        <div className="flex border-b border-gray-200 dark:border-[#243348] items-center">
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => { setSearchMode(key); setSearched(false); setResults([]); }}
              className={`flex-1 py-2.5 text-[var(--wm-text-ui)] font-medium transition-colors border-b-2 -mb-px
                ${searchMode === key
                  ? 'text-[#2E8BF0] border-[#2E8BF0]'
                  : 'text-[#64748B] dark:text-[#7D93AE] border-transparent hover:text-[#0F1923] dark:hover:text-[#F8FAFE]'
                }`}
            >
              {label}
            </button>
          ))}
          <div className="px-2 pb-px">
            <button
              onClick={() => setShowAI(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[var(--wm-text-ui)] font-medium bg-gradient-to-r from-blue-500/10 to-violet-500/10 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800/40 hover:from-blue-500/20 hover:to-violet-500/20 transition-all"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.25l1.09 4.47 4.47 1.09-4.47 1.09L12 13.41l-1.09-4.47-4.47-1.09 4.47-1.09L12 2.25z"/>
              </svg>
              AI Generate
            </button>
          </div>
        </div>

        <div className="p-4">
          {searchMode === 'vin' && (
            <div className="space-y-3">
              <div>
                <input
                  type="text"
                  value={vin}
                  onChange={(e) => {
                    setVin(e.target.value.toUpperCase());
                    setSearched(false);
                  }}
                  placeholder="Enter 17-character VIN"
                  maxLength={17}
                  className="w-full h-8 px-3 rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-[#0F1923] dark:text-[#F8FAFE] text-[var(--wm-text-body)] font-mono tracking-wider focus:outline-none focus:border-[#2E8BF0] focus:ring-1 focus:ring-[#2E8BF0] placeholder:text-[#64748B] dark:placeholder:text-[#7D93AE]"
                />
                <div className="flex justify-between mt-1.5 text-[var(--wm-text-ui)]">
                  <span className="text-[#64748B] dark:text-[#7D93AE]">17 characters required</span>
                  <span className={vin.length === 17 ? 'text-gray-600' : 'text-[#64748B] dark:text-[#7D93AE]'}>{vin.length}/17</span>
                </div>
              </div>
              <button
                onClick={handleVinSearch}
                disabled={loading || vin.length !== 17}
                className="w-full h-8 rounded bg-blue-600 text-white font-medium text-[var(--wm-text-ui)] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors"
              >
                {loading ? 'Searching...' : 'Search VIN'}
              </button>
            </div>
          )}

          {searchMode === 'browse' && (
            <YearMakeModelTrimPicker key="browse" onSelect={handleVehicleSelect} selectedCar={selectedCar} />
          )}

          {searchMode === 'image' && (
            <VehicleByImage key="image" onSelect={handleVehicleSelect} selectedCar={selectedCar} />
          )}
        </div>
      </div>

      {searchMode === 'vin' && selectedCar && !results.find(r => r.id === selectedCar.id || r.vin === selectedCar.vin) && (
        <div className="bg-blue-50 rounded p-3 border-l-2 border-blue-600">
          <p className="text-[var(--wm-text-ui)] text-gray-500 mb-0.5">Selected</p>
          <p className="text-gray-900 font-medium text-[var(--wm-text-body)]">
            {selectedCar.year} {selectedCar.make} {selectedCar.model}
          </p>
        </div>
      )}

      {searchMode === 'vin' && loading && (
        <div className="flex justify-center py-8">
          <div className="w-5 h-5 border border-gray-300 border-t-blue-600 rounded-full animate-spin" />
        </div>
      )}

      {/* NHTSA decoded result (fallback when not in Supabase DB) */}
      {searchMode === 'vin' && !loading && nhtsaResult && (
        <div className="bg-white dark:bg-gray-800 rounded border border-blue-200 dark:border-blue-800 overflow-hidden">
          <div className="px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800 flex items-center gap-2">
            <WMIcon name="check-circle" className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
            <span className="text-[var(--wm-text-ui)] font-medium text-blue-700 dark:text-blue-400">Decoded via NHTSA (not in local database)</span>
          </div>
          <div className="p-3 space-y-2">
            <div>
              <p className="font-medium text-[#0F1923] dark:text-[#F8FAFE] text-[var(--wm-text-body)]">
                {nhtsaResult.year} {nhtsaResult.make} {nhtsaResult.model}
                {nhtsaResult.trim && <span className="text-gray-500 dark:text-gray-400 ml-1 font-normal">{nhtsaResult.trim}</span>}
              </p>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {nhtsaResult.bodyStyle && <span className="text-[var(--wm-text-ui)] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">{nhtsaResult.bodyStyle}</span>}
                {nhtsaResult.driveType && <span className="text-[var(--wm-text-ui)] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">{nhtsaResult.driveType}</span>}
                {nhtsaResult.engine && <span className="text-[var(--wm-text-ui)] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">{nhtsaResult.engine}</span>}
              </div>
            </div>
            <p className="text-[var(--wm-text-caption)] text-amber-600 dark:text-amber-400">
              Note: Pricing data not available for this vehicle. You can still create an estimate with manual line items.
            </p>
            <button
              onClick={handleNhtsaSelect}
              className="w-full py-1.5 rounded bg-blue-600 text-white text-[var(--wm-text-ui)] font-medium hover:bg-blue-700 transition-colors"
            >
              Use This Vehicle
            </button>
          </div>
        </div>
      )}

      {searchMode === 'vin' && !loading && searched && results.length === 0 && !nhtsaResult && (
        <div className="bg-white dark:bg-gray-800 rounded p-6 text-center border border-gray-200 dark:border-gray-700">
          <p className="text-gray-500 dark:text-[#7D93AE] text-[var(--wm-text-body)] mb-1">No vehicle found</p>
          <p className="text-[#64748B] dark:text-[#7D93AE] text-[var(--wm-text-ui)]">
            VIN not found in database or NHTSA. Try browsing by year/make/model.
          </p>
        </div>
      )}

      {searchMode === 'vin' && results.length > 0 && (
        <div className="space-y-2">
          <p className="text-[var(--wm-text-ui)] text-gray-500 dark:text-[#7D93AE] px-1">
            {results.length} result{results.length !== 1 ? 's' : ''}
          </p>
          {results.map((car, index) => (
            <button
              key={car.id || car.vin || `car-${index}`}
              type="button"
              onClick={() => onSelect(car)}
              className={`w-full text-left rounded p-3 border transition-all ${
                selectedCar?.id === car.id || selectedCar?.vin === car.vin
                  ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-600'
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-blue-400'
              }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-[#0F1923] dark:text-[#F8FAFE] text-[var(--wm-text-body)]">
                    {car.year} {car.make} {car.model}
                  </p>
                  {car.trim && (
                    <p className="text-[var(--wm-text-ui)] text-gray-500 dark:text-[#7D93AE] mt-0.5">{car.trim}</p>
                  )}
                </div>
                <span className="text-[var(--wm-text-ui)] px-2 py-0.5 rounded border border-gray-200 dark:border-gray-600 text-gray-500 dark:text-[#7D93AE] bg-gray-50 dark:bg-gray-700 flex-shrink-0 ml-2">
                  {car.vehicle_type}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {showAI && (
        <AIEstimateGenerator
          onClose={() => setShowAI(false)}
          onUse={(draft) => {
            const parts = (draft.vehicleLabel || '').split(' ');
            const year = parseInt(parts[0]) || null;
            const make = parts[1] || '';
            const model = parts.slice(2).join(' ') || '';
            onSelect({
              year, make, model, trim: '',
              label: draft.vehicleLabel,
              _aiDraft: draft,
            });
            setShowAI(false);
          }}
          shopContext={(() => {
            try { return localStorage.getItem('wm-shop-profile') || ''; } catch { return ''; }
          })()}
          laborRates={(() => {
            try { return JSON.parse(localStorage.getItem('wm-labor-rates-v1') || '{}'); } catch { return {}; }
          })()}
        />
      )}
    </div>
  );
}
